import React, { useCallback } from "react";
import { Button, Grid, Loading, Typography } from "@sumprio/ui";
import SideModal from "./SideModal";
import SideModalFormFooter from "./SideModalFormFooter";

interface FormModalProps {
  open: boolean;
  title: string;
  primaryLabel: string;
  primaryDisabled?: boolean;
  isSubmitting?: boolean;
  submittingPrimaryLabel?: string;
  submittingOverlayText?: string;
  secondaryLabel?: string;
  clearDisabled?: boolean;
  cancelLabel?: string;
  sideSize?: "sm" | "md" | "lg" | "xl";
  showFooterDivider?: boolean;
  showHeaderDivider?: boolean;
  contentMountKey?: number;
  onClose: () => void;
  onPrimary: () => void;
  onSecondary?: () => void;
  children: React.ReactNode;
  primaryVariant?: React.ComponentProps<typeof Button>["variant"];
  primaryType?: React.ComponentProps<typeof Button>["type"];
  closeOnlyFooter?: boolean;
}

export default function FormModal({
  open,
  title,
  primaryLabel,
  primaryDisabled = false,
  isSubmitting = false,
  submittingPrimaryLabel,
  submittingOverlayText,
  secondaryLabel = "Clear",
  clearDisabled = false,
  cancelLabel,
  sideSize = "sm",
  showFooterDivider = true,
  showHeaderDivider = true,
  contentMountKey,
  onClose,
  onPrimary,
  onSecondary,
  children,
  primaryVariant,
  primaryType,
  closeOnlyFooter = false,
}: FormModalProps) {
  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    onClose();
  }, [isSubmitting, onClose]);

  const hasClearHandler = typeof onSecondary === "function";

  const footer = closeOnlyFooter ? (
    <Grid display="flex" justifyContent="flex-end" width="100%">
      <Button
        type={primaryType}
        variant={primaryVariant ?? "brand-default"}
        size="xs"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleClose();
        }}
      >
        {primaryLabel}
      </Button>
    </Grid>
  ) : (
    <SideModalFormFooter
      onCancel={handleClose}
      cancelLabel={cancelLabel}
      onMiddle={() => {
        if (typeof onSecondary === "function") onSecondary();
      }}
      middleLabel={secondaryLabel}
      middleDisabled={!hasClearHandler || isSubmitting || clearDisabled}
      onPrimary={onPrimary}
      primaryLabel={primaryLabel}
      primaryDisabled={primaryDisabled}
      primaryLoading={isSubmitting}
      primaryLoadingLabel={submittingPrimaryLabel}
      cancelDisabled={isSubmitting}
      primaryVariant={primaryVariant}
      primaryType={primaryType}
    />
  );

  return (
    <SideModal
      open={open}
      onClose={handleClose}
      title={title}
      footer={footer}
      sideSize={sideSize}
      showFooterDivider={showFooterDivider}
      showHeaderDivider={showHeaderDivider}
      showCloseButton={!closeOnlyFooter}
    >
      <div style={{ position: "relative", minHeight: "100%" }}>
        {isSubmitting ? (
          <div
            aria-busy="true"
            aria-live="polite"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 6,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "var(--spacing-md, 12px)",
              background: "rgba(255, 255, 255, 0.72)",
            }}
          >
            <Loading variant="lineSpinner" size="md" />
            {submittingOverlayText ? (
              <Typography variant="body" bodySize="sm" color="gray" style={{ textAlign: "center" }}>
                {submittingOverlayText}
              </Typography>
            ) : null}
          </div>
        ) : null}
        <div
          style={{ position: "relative", zIndex: 1 }}
          {...(contentMountKey !== undefined ? { key: contentMountKey } : {})}
        >
          {children}
        </div>
      </div>
    </SideModal>
  );
}
